import { useEffect } from 'react';
import {
  SpaceBetween,
  Button,
  Textarea,
  Form,
  FormField,
  Alert,
  Box,
  StatusIndicator,
} from '@cloudscape-design/components';
import z from 'zod';
import { PutFeedbackCommandInput } from '@amzn/gbsa-helios-typescript-client';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFeedbackQuery } from 'src/data/api/helios-service-ts/feedback/queries/useFeedbackQuery';
import { usePutFeedbackMutation } from 'src/data/api/helios-service-ts/feedback/queries/usePutFeedbackMutation';

interface FeedbackPopoverProps {
  auditId: string;
  questionId: string;
  businessUnitId: string;
  divisionId: string;
  createdBy: string;
  auditName: string;
  templateName: string;
  location: string;
  questionTitle: string;
  questionVersion: string;
  onClose: () => void;
}

const feedbackForm = z.object({
  auditId: z.string(),
  createdBy: z.string(),
  feedback: z
    .string({
      error: 'Feedback is required',
    })
    .min(12, {
      error: 'Feedback must be at least 12 characters long',
    }),
  questionId: z.string(),
  businessUnitId: z.string(),
  divisionId: z.string(),
  auditName: z.string(),
  templateName: z.string(),
  location: z.string(),
  questionTitle: z.string(),
  questionVersion: z.string(),
}) satisfies z.ZodType<PutFeedbackCommandInput>;
export function FeedbackForm({
  auditId,
  questionId,
  businessUnitId,
  divisionId,
  createdBy,
  auditName,
  templateName,
  location,
  questionTitle,
  questionVersion,
  onClose,
}: FeedbackPopoverProps) {
  const { data: feedback, isLoading: isLoadingFeedback } = useFeedbackQuery({
    auditId,
    createdBy,
    questionId,
    businessUnitId,
    divisionId,
  });

  const form = useForm({
    resolver: zodResolver(feedbackForm),
    defaultValues: {
      auditId,
      questionId,
      feedback: '',
      businessUnitId,
      divisionId,
      createdBy,
      auditName,
      templateName,
      location,
      questionTitle,
      questionVersion,
    },
  });
  useEffect(() => {
    if (isLoadingFeedback || !feedback) return;
    form.setValue('feedback', feedback.feedback);
  }, [feedback, isLoadingFeedback, form]);

  const {
    mutate: putFeedbackMutation,
    status: putFeedbackStatus,
    error: putFeedbackError,
  } = usePutFeedbackMutation(
    {
      auditId,
      createdBy,
      questionId,
      businessUnitId,
      divisionId,
    },
    {
      onSuccess: () => {
        onClose();
      },
    },
  );
  if (putFeedbackStatus === 'success') {
    return (
      <StatusIndicator type="success">Feedback submitted successfully</StatusIndicator>
    );
  }

  return (
    <form
      onSubmit={form.handleSubmit((data) => putFeedbackMutation(data))}
      id="feedback-form"
    >
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              formAction="none"
              variant="link"
              onClick={() => {
                form.setValue('feedback', '');
                onClose();
              }}
              loading={putFeedbackStatus === 'pending'}
              disabled={putFeedbackStatus === 'pending'}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              form="feedback-form"
              formAction="submit"
              loading={putFeedbackStatus === 'pending'}
              disabled={putFeedbackStatus === 'pending'}
            >
              Submit
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween direction="vertical" size="l">
          <Controller
            name="feedback"
            control={form.control}
            render={({ field }) => (
              <FormField errorText={form.formState.errors.feedback?.message}>
                <Textarea
                  placeholder="Please provide your feedback..."
                  {...field}
                  value={field.value}
                  onChange={({ detail }) => field.onChange(detail.value)}
                  rows={4}
                />
              </FormField>
            )}
          />
        </SpaceBetween>
      </Form>

      {putFeedbackStatus === 'error' ? (
        <Box margin={{ top: 'xl' }}>
          <Alert type="error">
            {putFeedbackError?.message ?? 'An error occurred while submitting feedback'}
          </Alert>
        </Box>
      ) : null}
    </form>
  );
}
